// The grid's own reading of its sub-questions: a strip carrying the metric selector and the unit
// switch, and the table they drive, between the aggregate chart and the sub-question list.
//
// The controls sit here rather than in the chart's control strip, where every other block keeps its
// metric control. A sub-question's own selector governs that sub-question's chart, so it belongs on
// it; these govern the table below and would otherwise sit a chart's height away from what they
// move. It also keeps the two levels apart: a reader sees one control on the chart and one on the
// table, and never has to work out which of them a click will move.
//
// Spec: specs/stats/questions/summary_metrics/functional_requirements.md §6.6, §6.9

import classNames from 'classnames'
import PropTypes from 'prop-types'

import Table from '@aytm/app/stats/components/data-views/table/components/Table'
import MetricsDropdown from '@aytm/app/stats/page/question-views/mx/components/summary/MetricsDropdown'
import { hasBox } from '@aytm/app/stats/page/question-views/mx/lib/summary/params'
import { AMOUNTS, PERCENTS } from '@aytm/app/stats/page/question-views/mx/lib/summary/table'

import style from '@aytm/app/stats/page/question-views/mx/components/summary/styles/Summary.module.scss'

// the box figures have both readings, so the switch governs the whole table rather than any one
// column; the distribution figures are positions on the scale and ignore it
const UNITS = [
  { value: AMOUNTS, text: 'N' },
  { value: PERCENTS, text: '%' }
]

const Summary = ({ table, metrics, selection, unit, disabled, onChange, onUnitChange }) => {
  if (!metrics?.items?.length) return null

  const nothingSelected = !selection?.keys?.length

  // with every metric off, or only distribution figures on, the unit has nothing to state; the pair
  // stays in its place, disabled, so the strip does not shift each time a metric comes or goes
  const boxSelected = hasBox(selection?.keys || [], metrics)

  return (
    <div className={style.summary}>
      <div className={style.controls}>
        {/* this selector governs a table, not a chart: there are no answer columns to hide, and
            every metric it lists is a figure, so marking the figure-only ones would mark them all */}
        <MetricsDropdown
          governsChart={false}
          metrics={metrics}
          selection={selection}
          disabled={disabled}
          onChange={onChange}
        />

        <div className={style.units}>
          {UNITS.map(option => (
            <button
              key={option.value}
              type="button"
              disabled={disabled || !boxSelected}
              // greyed only for want of a figure to state; a disabled question keeps the plain inert
              // look its selector has
              className={classNames(style.unit, {
                [style.unitActive]: option.value === unit,
                [style.unitIdle]: !boxSelected && !disabled
              })}
              onClick={() => onUnitChange(option.value)}
            >
              {option.text}
            </button>
          ))}
        </div>
      </div>

      {/* the strip stays when every metric is off, or there would be no way back to one, and a
          line in the table's place says where that way is -- except on a disabled question, whose
          selector cannot be opened */}
      {table ? <Table {...table} /> : null}
      {nothingSelected ? (
        <div className={style.empty}>
          {disabled
            ? 'No metrics selected.'
            : "No metrics selected. Select as many as you'd like from the Metrics dropdown above."}
        </div>
      ) : null}
    </div>
  )
}

Summary.displayName = 'MatrixSummary'

Summary.propTypes = {
  table: PropTypes.object,
  metrics: PropTypes.object,
  selection: PropTypes.object,
  unit: PropTypes.string,
  disabled: PropTypes.bool,
  onChange: PropTypes.func,
  onUnitChange: PropTypes.func
}

export default Summary
